import React, { useEffect, useMemo, useRef, useState } from "react";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import * as Icons from "lucide-react";
import { useAuth } from "../../hooks/useAuth";
import { usePermissions } from "../../hooks/usePermissions";
import {
  ADMIN_NAV_GROUPS,
  HEADER_NAV_GROUPS,
  HeaderNavGroup,
  NavItem,
  ROLE_LABELS,
  isAdmin,
  isSuperAdmin,
} from "../../utils/roles";
import { Role } from "../../types";
import { apiList } from "../../api/apiClient";
import { visitorOrigin } from "../../utils/surface";
import "./ShadowShell.css";
import "./ShadowEnhancements.css";
import "./ShadowMobile.css";
import "./PrototypePages.css";

const IS_ADMIN_SURFACE =
  process.env.REACT_APP_BUILD_TARGET === "admin" ||
  (typeof window !== "undefined" &&
    window.location.hostname.startsWith("shadow.admin."));
const SHOP_GROUP: HeaderNavGroup = {
  label: "Signs",
  items: [
    {
      label: "Sign Shop",
      path: "/sign-shop",
      icon: "shopping-cart",
      check: isAdmin,
    },
  ],
};
const INTEGRATIONS_ITEM: NavItem = {
  label: "Integrations",
  path: "/integrations",
  icon: "chart",
  check: () => true,
};
const iconMap: Record<string, React.ElementType> = {
  home: Icons.LayoutDashboard,
  users: Icons.Users,
  building: Icons.Building2,
  "map-pin": Icons.Building,
  user: Icons.UserRound,
  "guest-key": Icons.KeyRound,
  "user-cog": Icons.UserCog,
  camera: Icons.Camera,
  cpu: Icons.RadioTower,
  shield: Icons.KeyRound,
  smartphone: Icons.Smartphone,
  settings: Icons.ShieldCheck,
  clock: Icons.History,
  chart: Icons.BarChart3,
  dollar: Icons.CreditCard,
  mail: Icons.Mail,
  send: Icons.Send,
  "shopping-cart": Icons.ShoppingBag,
};
const routeCopy: Record<string, [string, string]> = {
  "/admin": ["Dashboard", "Portfolio health and current operations"],
  "/dashboard": ["Dashboard", "Portfolio health and current operations"],
  "/integrators": [
    "Dealers",
    "Manage dealer organizations and their portfolios",
  ],
  "/customers": ["Customers", "Customer organizations across your portfolio"],
  "/properties": [
    "Properties",
    "Buildings, communities, and operational status",
  ],
  "/tenants": ["People", "Residents, tenants, and property users"],
  "/guests": ["Guests", "Temporary visitor access and secure passes"],
  "/deliveries": ["Deliveries", "Delivery access codes and activity"],
  "/vendors": ["Vendors", "Vendor organizations, employees, and access"],
  "/web-users": ["Web Users", "Users who can access the management application"],
  "/devices": ["Devices", "Connected hardware and real-time health"],
  "/access-control": [
    "Access Control",
    "Doors, readers, credentials, and access systems",
  ],
  "/cameras": ["Cameras", "Video devices and live stream availability"],
  "/intercoms": ["Intercoms", "Visitor directories and intercom configuration"],
  "/mobile-users": ["Mobile App", "Mobile activations and device status"],
  "/call-history": ["Call History", "Voice and video call activity"],
  "/device-history": ["Device History", "Access and device activity"],
  "/reports": ["Reports", "Operational reporting across the accessible portfolio"],
  "/rules": ["Rules", "Automated access policies and schedules"],
  "/notifications": ["Notifications", "Operational alerts and updates"],
  "/integrations": ["Integrations", "Opt-in providers, authorization, and property assignments"],
  "/settings": ["Settings", "Account, security, and application preferences"],
};
const itemsOf = (g: HeaderNavGroup): NavItem[] => [
  ...(g.items || []),
  ...(g.columns || []).flatMap((c) => c.items),
];
const active = (pathname: string, path?: string) =>
  !!path && (pathname === path || pathname.startsWith(`${path}/`));

type SearchRecord = {
  key: string;
  type: string;
  label: string;
  detail: string;
  path: string;
  haystack: string;
};
const SEARCH_SOURCES = [
  {
    resource: "integrators",
    type: "Dealer",
    gate: "/integrators",
    base: "/integrators",
    label: ["company", "company_name", "name"],
    detail: ["email", "phone", "city", "state"],
  },
  {
    resource: "customers",
    type: "Customer",
    gate: "/customers",
    base: "/customers",
    label: ["company", "company_name", "name"],
    detail: ["email", "phone", "city", "state"],
  },
  {
    resource: "properties",
    type: "Property",
    gate: "/properties",
    base: "/properties",
    label: ["name", "property_name"],
    detail: ["address", "city", "state", "zip_code"],
  },
  {
    resource: "tenants",
    type: "Person",
    gate: "/tenants",
    base: "/tenants",
    label: ["name", "full_name", "first_name"],
    detail: ["last_name", "email", "phone", "unit_number"],
  },
  {
    resource: "devices",
    type: "Device",
    gate: "/devices",
    base: "/devices",
    label: ["name", "device_name"],
    detail: ["manufacturer", "model", "serial_number", "status"],
  },
  {
    resource: "units",
    type: "Unit",
    gate: "/properties",
    base: "/units",
    label: ["name", "unit_number"],
    detail: ["building", "floor"],
  },
  {
    resource: "users",
    type: "Web user",
    gate: "/web-users",
    base: "/web-users",
    label: ["name", "full_name", "email"],
    detail: ["email", "role"],
  },
  {
    resource: "guest_pins",
    type: "Guest",
    gate: "/guests",
    base: "/guests",
    label: ["guest_name", "name"],
    detail: ["company", "status"],
  },
  {
    resource: "intercom_configs",
    type: "Intercom",
    gate: "/intercoms",
    base: "/intercoms",
    label: ["name", "display_name"],
    detail: ["phone_number", "status"],
  },
  {
    resource: "access_control_systems",
    type: "Access system",
    gate: "/access-control",
    base: "/access-control",
    label: ["name", "system_name"],
    detail: ["provider", "status"],
  },
] as const;
const clean = (value: unknown) => String(value ?? "").trim();
const first = (row: any, fields: readonly string[]) =>
  fields.map((f) => clean(row[f])).find(Boolean) || "";
const wordsOf = (value: string) => value.toLowerCase().normalize("NFKD").replace(/[\u0300-\u036f]/g, "").match(/[a-z0-9]+/g) || [];
const editDistance = (a: string, b: string) => {
  const row = Array.from({ length: b.length + 1 }, (_, i) => i);
  for (let i = 1; i <= a.length; i++) {
    let diagonal = row[0]; row[0] = i;
    for (let j = 1; j <= b.length; j++) {
      const above = row[j];
      row[j] = Math.min(row[j] + 1, row[j - 1] + 1, diagonal + (a[i - 1] === b[j - 1] ? 0 : 1));
      diagonal = above;
    }
  }
  return row[b.length];
};
export const fuzzyScore = (text: string, query: string) => {
  const value = wordsOf(text); const wanted = wordsOf(query);
  if (!wanted.length) return 0;
  const phrase = value.join(" "); const exactPhrase = wanted.join(" ");
  if (phrase.includes(exactPhrase)) return 1000 - phrase.indexOf(exactPhrase);
  const available = new Set(value.map((_, index) => index)); let total = 0;
  for (const token of wanted) {
    let bestIndex = -1; let bestScore = -1;
    for (const index of Array.from(available)) {
      const candidate = value[index]; let score = -1;
      if (candidate === token) score = 300;
      else if (token.length >= 3 && (candidate.startsWith(token) || token.startsWith(candidate))) score = 220 - Math.abs(candidate.length - token.length);
      else if (token.length >= 4 && candidate.includes(token)) score = 180 - candidate.indexOf(token);
      else {
        const allowed = token.length <= 4 ? 1 : Math.min(2, Math.floor(token.length / 4));
        const distance = Math.abs(candidate.length - token.length) <= allowed ? editDistance(candidate, token) : allowed + 1;
        if (distance <= allowed) score = 140 - distance * 25;
      }
      if (score > bestScore) { bestScore = score; bestIndex = index; }
    }
    if (bestIndex < 0 || bestScore < 0) return -1;
    available.delete(bestIndex); total += bestScore;
  }
  return total;
};

export const ShadowShell: React.FC = () => {
  const { user, logout } = useAuth();
  const { canView } = usePermissions();
  const location = useLocation();
  const navigate = useNavigate();
  const searchRef = useRef<HTMLInputElement>(null);
  const role = (user?.role || "customer") as Role;
  const [mobileOpen, setMobileOpen] = useState(false);
  const favoritesKey = IS_ADMIN_SURFACE ? "intrlock_admin_favorites" : "intrlock_app_favorites";
  const dashboardPath = IS_ADMIN_SURFACE ? "/admin" : "/dashboard";
  const [favoritePaths, setFavoritePaths] = useState<string[]>(() => {
    try {
      const raw = localStorage.getItem(favoritesKey);
      if (raw === null) return [dashboardPath];
      const stored = JSON.parse(raw);
      return Array.isArray(stored) ? Array.from(new Set(stored)) : [dashboardPath];
    } catch { return [dashboardPath]; }
  });
  const [draggedFavorite, setDraggedFavorite] = useState<string | null>(null);
  const baseGroups = useMemo(() => {
    const dashboardItem: NavItem = { label: "Dashboard", path: dashboardPath, icon: "home", check: () => true };
    const source = IS_ADMIN_SURFACE
      ? ADMIN_NAV_GROUPS
      : (() => { const list = [...HEADER_NAV_GROUPS]; return [...list.slice(0, -1), SHOP_GROUP, list[list.length - 1]]; })();
    return source
      .filter(group => group.label !== "Dashboard" && group.path !== dashboardPath)
      .map(group => group.label === "Organization"
        ? { ...group, items: [dashboardItem, ...itemsOf(group)] }
        : group.label === "System"
          ? { ...group, items: [INTEGRATIONS_ITEM, ...itemsOf(group)] }
          : group);
  }, [dashboardPath]);
  const groups = useMemo(
    () =>
      baseGroups
        .map((g) => ({
          ...g,
          items: itemsOf(g).filter((i) => {
            // Super admins must retain access to every super-admin route even
            // when an older permissions row contains an explicit false value.
            // Those rows are role/user overrides for scoped users, not a way
            // to remove core administration pages from the platform owner.
            if (isSuperAdmin(role)) return i.check(role);
            const override = canView(i.path.replace(/^\//, ""));
            return override !== undefined ? override : i.check(role);
          }),
          columns: undefined,
        }))
        .filter((g) => g.path || (g.items?.length || 0) > 0),
    [baseGroups, canView, role],
  );
  useEffect(() => {
    localStorage.setItem(favoritesKey, JSON.stringify(favoritePaths));
  }, [favoritePaths, favoritesKey]);
  const allNavItems = useMemo<NavItem[]>(() => groups.flatMap((group) => group.path
    ? [{ label: group.label, path: group.path, icon: "home", check: () => true }]
    : itemsOf(group)), [groups]);
  const favoriteItems = useMemo(() => favoritePaths
    .map((path) => allNavItems.find((item) => item.path === path))
    .filter((item): item is NavItem => Boolean(item)), [allNavItems, favoritePaths]);
  const toggleFavorite = (path: string) => setFavoritePaths((old) => {
    const next = old.includes(path)
      ? old.filter((value) => value !== path)
      : Array.from(new Set([...old, path]));
    // Persist inside the interaction rather than waiting for an effect. This
    // guarantees an immediate sign-out cannot race the favorites write.
    localStorage.setItem(favoritesKey, JSON.stringify(next));
    return next;
  });
  const moveFavorite = (sourcePath: string, targetPath: string) => {
    if (!sourcePath || sourcePath === targetPath) return;
    setFavoritePaths((old) => {
      const from = old.indexOf(sourcePath);
      const to = old.indexOf(targetPath);
      if (from < 0 || to < 0) return old;
      const next = [...old];
      next.splice(from, 1);
      next.splice(to, 0, sourcePath);
      localStorage.setItem(favoritesKey, JSON.stringify(next));
      return next;
    });
  };
  const currentGroup = groups.find(
    (g) =>
      active(location.pathname, g.path) ||
      g.items?.some((i) => active(location.pathname, i.path)),
  );
  const currentItem = groups
    .flatMap(itemsOf)
    .find((i) => active(location.pathname, i.path));
  const [expanded, setExpanded] = useState<Set<string>>(
    () => new Set(["Favorites"]),
  );
  useEffect(() => {
    if (!currentGroup?.label) return;
    setExpanded((old) => {
      if (old.has(currentGroup.label)) return old;
      const next = new Set(old);
      next.add(currentGroup.label);
      return next;
    });
  }, [location.pathname, currentGroup?.label]);
  const [query, setQuery] = useState("");
  const [records, setRecords] = useState<SearchRecord[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchLoaded, setSearchLoaded] = useState(false);
  const [searchError, setSearchError] = useState(false);
  const [activeResult, setActiveResult] = useState(0);
  const allowedPaths = useMemo(
    () =>
      new Set(
        groups.flatMap((g) =>
          [g.path, ...itemsOf(g).map((i) => i.path)].filter(Boolean),
        ),
      ),
    [groups],
  );
  const matching = useMemo(() => {
    if (!query.trim()) return [];
    const navigation: SearchRecord[] = groups
      .flatMap(itemsOf)
      .map((i) => ({
        key: `nav:${i.path}`,
        type: "Page",
        label: i.label,
        detail: i.path,
        path: i.path,
        haystack: `${i.label} ${i.path}`,
      }));
    return [...navigation, ...records]
      .map((item) => ({ item, score: fuzzyScore(item.haystack, query) }))
      .filter((x) => x.score >= 0)
      .sort(
        (a, b) => b.score - a.score || a.item.label.localeCompare(b.item.label),
      )
      .slice(0, 12)
      .map((x) => x.item);
  }, [groups, query, records]);
  const routeCopyKey = Object.keys(routeCopy)
    .filter(path => location.pathname === path || location.pathname.startsWith(`${path}/`))
    .sort((a, b) => b.length - a.length)[0];
  const copy = routeCopy[routeCopyKey || currentItem?.path || currentGroup?.path || ""] || [
    currentItem?.label || currentGroup?.label || "Intrlock",
    "Secure property operations",
  ];
  const initials = (user?.name || user?.email || "Intrlock User")
    .split(/[\s@]+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((s) => s[0])
    .join("")
    .toUpperCase();
  useEffect(() => setMobileOpen(false), [location.pathname]);
  useEffect(() => {
    if (!mobileOpen) return;
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") setMobileOpen(false);
    };
    window.addEventListener("keydown", closeOnEscape);
    return () => window.removeEventListener("keydown", closeOnEscape);
  }, [mobileOpen]);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        searchRef.current?.focus();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);
  useEffect(() => {
    setActiveResult(0);
  }, [query]);
  const loadSearchData = async () => {
    if (searchLoaded || searchLoading) return;
    setSearchLoading(true);
    setSearchError(false);
    try {
      const sources = SEARCH_SOURCES.filter((source) =>
        allowedPaths.has(source.gate),
      );
      const lists = await Promise.all(
        sources.map((source) =>
          apiList(source.resource, { per_page: 1000 }).catch(() => []),
        ),
      );
      const next = sources.flatMap((source, index) =>
        lists[index].map((row: any, rowIndex: number) => {
          const id = clean(row.uuid || row.id);
          const label =
            first(row, source.label) || `${source.type} ${rowIndex + 1}`;
          const detail = source.detail
            .map((field) => clean(row[field]))
            .filter(Boolean)
            .join(" · ");
          const propertyId = clean(row.property_uuid || row.property_id);
          const detailPath = source.resource === "intercom_configs" && id && propertyId
            ? `${visitorOrigin()}/${encodeURIComponent(propertyId)}/i/${encodeURIComponent(id)}`
            : id && ["/integrators", "/customers", "/properties", "/tenants"].includes(source.base)
              ? `${source.base}/${encodeURIComponent(id)}`
              : `${source.base}?search=${encodeURIComponent(label)}`;
          return {
            key: `${source.resource}:${id || rowIndex}`,
            type: source.type,
            label,
            detail,
            path: detailPath,
            haystack: [label, detail, id].join(" "),
          };
        }),
      );
      setRecords(next);
      setSearchLoaded(true);
    } catch {
      setSearchError(true);
    } finally {
      setSearchLoading(false);
    }
  };
  const go = (path: string) => {
    setQuery("");
    if (/^https?:\/\//i.test(path)) {
      window.open(path, "_blank", "noopener,noreferrer");
      return;
    }
    navigate(path);
  };
  const navRow = (item: NavItem, favoriteCopy = false) => {
    const Icon = iconMap[item.icon] || Icons.Circle;
    const selected = favoritePaths.includes(item.path);
    return <div
      className={`shadow-nav-row ${favoriteCopy ? 'shadow-favorite-row' : ''}`}
      key={`${favoriteCopy ? "favorite" : "nav"}:${item.path}`}
      draggable={favoriteCopy}
      onDragStart={favoriteCopy ? (event) => { setDraggedFavorite(item.path); event.dataTransfer.effectAllowed = 'move'; event.dataTransfer.setData('text/plain', item.path); } : undefined}
      onDragOver={favoriteCopy ? (event) => { event.preventDefault(); event.dataTransfer.dropEffect = 'move'; } : undefined}
      onDrop={favoriteCopy ? (event) => { event.preventDefault(); moveFavorite(event.dataTransfer.getData('text/plain') || draggedFavorite || '', item.path); setDraggedFavorite(null); } : undefined}
      onDragEnd={favoriteCopy ? () => setDraggedFavorite(null) : undefined}
    >
      <NavLink to={item.path} className={({ isActive }) => `shadow-nav-item ${isActive && (favoriteCopy || !selected) ? "is-active" : ""}`}>
        <Icon /><span>{item.label}</span>
      </NavLink>
      {!favoriteCopy && <button type="button" className={`shadow-favorite-toggle ${selected ? "is-favorite" : ""}`} onClick={() => toggleFavorite(item.path)} aria-label={`${selected ? "Remove" : "Add"} ${item.label} ${selected ? "from" : "to"} Favorites`} title={`${selected ? "Remove from" : "Add to"} Favorites`}>
        <img src="/intrlock-cloud-favorite.svg" alt="" />
      </button>}
    </div>;
  };
  const navigation = () => (
    <nav className="shadow-nav" aria-label="Primary navigation">
      <div className="shadow-nav-group">
        <button
          className="shadow-nav-label shadow-nav-toggle"
          onClick={() => setExpanded((old) => {
            const next = new Set(old);
            next.has("Favorites") ? next.delete("Favorites") : next.add("Favorites");
            return next;
          })}
        >
          <span>Favorites</span>
          <Icons.ChevronDown className={expanded.has("Favorites") ? "open" : ""} />
        </button>
        {expanded.has("Favorites") && favoriteItems.map((item) => navRow(item, true))}
      </div>
      {groups.map((group) =>
        group.label === "Dashboard" || (group.path === dashboardPath) ? null : group.path ? (
          <div className="shadow-nav-group" key={group.label}>
            <div className="shadow-nav-label">{group.label}</div>
            {navRow({ label: group.label, path: group.path, icon: "home", check: () => true })}
          </div>
        ) : (
          <div className="shadow-nav-group" key={group.label}>
            <button
              className="shadow-nav-label shadow-nav-toggle"
              onClick={() =>
                setExpanded((old) => {
                  const n = new Set(old);
                  n.has(group.label)
                    ? n.delete(group.label)
                    : n.add(group.label);
                  return n;
                })
              }
            >
              <span>
                {group.label === "System"
                  ? "Systems"
                  : group.label === "History"
                    ? "History & Reports"
                    : group.label}
              </span>
              <Icons.ChevronDown
                className={expanded.has(group.label) ? "open" : ""}
              />
            </button>
            {expanded.has(group.label) &&
              group.items?.map((item) => navRow(item))}
          </div>
        ),
      )}
    </nav>
  );
  return (
    <div className="shadow-app-shell">
      {mobileOpen && (
        <button
          className="shadow-scrim"
          aria-label="Close navigation"
          onClick={() => setMobileOpen(false)}
        />
      )}
      <aside id="shadow-primary-navigation" className={`shadow-sidebar ${mobileOpen ? "is-open" : ""}`}>
        <div className="shadow-brand">
          <img src="/intrlock-banner-white.png" alt="Intrlock" />
          <button onClick={() => setMobileOpen(false)} aria-label="Close">
            <Icons.X />
          </button>
        </div>
        {navigation()}
        <div className="shadow-user">
          <span className="shadow-avatar mint">{initials}</span>
          <span className="shadow-user-copy">
            <strong>{user?.name || user?.email || "Intrlock User"}</strong>
            <small>{ROLE_LABELS[role]}</small>
          </span>
          <button
            aria-label="Sign out"
            title="Sign out"
            onClick={() => {
              logout();
              navigate("/login");
            }}
          >
            <Icons.LogOut />
          </button>
        </div>
      </aside>
      <div className="shadow-workspace">
        <header className="shadow-topbar">
          <button
            className="shadow-menu"
            aria-label={mobileOpen ? "Close navigation" : "Open navigation"}
            aria-controls="shadow-primary-navigation"
            aria-expanded={mobileOpen}
            onClick={() => setMobileOpen(open => !open)}
          >
            <Icons.Menu />
          </button>
          <img
            className="shadow-mobile-logo"
            src="/intrlock-banner-light.png"
            alt="Intrlock"
          />
          <div className="shadow-page-heading">
            <span>
              Intrlock {IS_ADMIN_SURFACE ? "Admin" : "App"} /{" "}
              {currentGroup?.label || copy[0]}
            </span>
            <h1>{copy[0]}</h1>
            <p>{copy[1]}</p>
          </div>
          <div className="shadow-actions">
            <div className="shadow-search">
              <Icons.Search />
              <input
                ref={searchRef}
                value={query}
                onFocus={loadSearchData}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "ArrowDown") {
                    e.preventDefault();
                    setActiveResult((i) =>
                      Math.min(i + 1, matching.length - 1),
                    );
                  }
                  if (e.key === "ArrowUp") {
                    e.preventDefault();
                    setActiveResult((i) => Math.max(i - 1, 0));
                  }
                  if (e.key === "Enter" && matching[activeResult]) {
                    e.preventDefault();
                    go(matching[activeResult].path);
                  }
                  if (e.key === "Escape") setQuery("");
                }}
                placeholder="Search all data"
                aria-label="Search all accessible Intrlock data"
                  aria-autocomplete="list"
                />
                {query && <button type="button" className="shadow-search-clear" aria-label="Clear universal search" title="Clear search" onClick={() => { setQuery(""); requestAnimationFrame(() => searchRef.current?.focus()); }}><Icons.X aria-hidden="true" /></button>}
                <kbd>⌘K</kbd>
              {query.trim() && (
                <div className="shadow-search-results" role="listbox">
                  {searchLoading && (
                    <div className="shadow-search-state">
                      <Icons.Loader2 className="spin" /> Loading accessible
                      data…
                    </div>
                  )}
                  {!searchLoading && searchError && (
                    <div className="shadow-search-state error">
                      Some data could not be searched. Try again.
                    </div>
                  )}
                  {!searchLoading &&
                    matching.map((item, index) => (
                      <button
                        role="option"
                        aria-selected={index === activeResult}
                        className={index === activeResult ? "selected" : ""}
                        key={item.key}
                        onMouseEnter={() => setActiveResult(index)}
                        onClick={() => go(item.path)}
                      >
                        <span className="shadow-result-type">{item.type}</span>
                        <strong>{item.label}</strong>
                        <small>{item.detail || item.path}</small>
                      </button>
                    ))}
                  {!searchLoading && matching.length === 0 && (
                    <div className="shadow-search-state">
                      No accessible records match “{query}”.
                    </div>
                  )}
                </div>
              )}
            </div>
            <button
              className="shadow-icon-button"
              aria-label="Notifications"
              onClick={() => navigate("/notifications")}
            >
              <Icons.Bell />
            </button>
            <span className="shadow-avatar">{initials}</span>
          </div>
        </header>
        <main className="shadow-content">
          <Outlet />
        </main>
      </div>
    </div>
  );
};
